// Resilience guard for third-party DOM mutation (Google Translate et al.).
//
// Browser translation extensions — Google Translate is the canonical case —
// rewrite the page's text nodes, wrapping each one in a <font> element. That
// reparents the node OUTSIDE React's knowledge. When React later reconciles
// (unmounting a component, swapping an AnimatePresence child, re-rendering a
// conditional) its commit phase calls `parent.removeChild(node)` /
// `parent.insertBefore(node, ref)` assuming `node` is still a direct child of
// `parent`. After the extension moved it, it isn't — so the native call throws
// `TypeError: Cannot read properties of null (reading 'removeChild')` and the
// entire React tree unmounts into a blank screen (Sentry issue 124350175).
//
// The fix — recommended by the React team for exactly this scenario — makes the
// two native mutators no-op when the node isn't actually a child of the node
// they're called on, instead of throwing. Correct React code never hits this
// branch (`child.parentNode === this` always holds for its own renders), so our
// own reconciliation is unaffected; only the extension-induced mismatch is
// absorbed, which keeps the page rendered AND browser translation working.
//
// Must run before React hydration, which is why it's imported first in
// instrumentation-client.ts (alongside the IntersectionObserver polyfill).
//
// Logging note: instrumentation-client.ts wires Sentry's
// captureConsoleIntegration at the "error" level, so logging here with
// console.error would re-create the very Sentry event we're preventing. We warn
// once per page load instead (warn isn't captured) — enough to surface the guard
// firing in the console without spamming it for every reparented node.

let warned = false

function warnOnce(): void {
  if (warned || typeof console === "undefined") return
  warned = true
  console.warn(
    "[extension-dom-guard] Suppressed a DOM mutation on a node reparented by a " +
      "browser extension (e.g. Google Translate). See lib/extension-dom-guard.ts."
  )
}

if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function removeChildGuarded<T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      warnOnce()
      return child
    }
    // Native removeChild returns the removed node, i.e. `child` itself.
    originalRemoveChild.call(this, child)
    return child
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function insertBeforeGuarded<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      warnOnce()
      return newNode
    }
    // Native insertBefore returns the inserted node, i.e. `newNode` itself.
    originalInsertBefore.call(this, newNode, referenceNode)
    return newNode
  }
}
